import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";

const AIRPORTS = ["LHR", "EMA"];
const REFRESH_MS = 60 * 1000;

const SYSTEM_DASHBOARDS = [
  { key: "flight", label: "Flight", path: "/api/airport-ops/airports/:airport/alerts/dashboard" },
  { key: "road", label: "Road", path: "/api/road-corridors/airports/:airport/alerts/dashboard" },
  { key: "rail", label: "Rail", path: "/api/rail-corridors/airports/:airport/alerts/dashboard" },
  { key: "weather", label: "Weather", path: "/api/airport-weather/airports/:airport/alerts/dashboard" },
  { key: "tracking", label: "Tracking", path: "/api/airport-tracking/airports/:airport/alerts/dashboard" },
  { key: "manual", label: "Manual", path: "/api/manual-airport-alerts/airports/:airport/dashboard" },
];

const severityRank = {
  critical_red: 5,
  red: 4,
  amber: 3,
  light_green: 2,
  green: 1,
  cleared: 0,
  unknown: 0,
};

function normalizeSeverity(value) {
  const raw = String(value || "unknown").trim().toLowerCase();
  if (raw === "critical") return "critical_red";
  if (raw === "yellow") return "amber";
  return raw || "unknown";
}

function alertTime(alert) {
  return (
    alert.last_seen_at ||
    alert.last_observed_at ||
    alert.last_processed_at ||
    alert.opened_at ||
    alert.observed_at_utc ||
    alert.updated_at ||
    alert.created_at ||
    null
  );
}

function timestamp(alert) {
  const parsed = Date.parse(alertTime(alert) || "");
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDateTime(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
  }).format(date);
}

function todayKey(value = Date.now()) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusLabel(value) {
  const normalized = normalizeSeverity(value);
  if (normalized === "critical_red") return "Critical";
  if (normalized === "light_green") return "Light";
  if (normalized === "unknown") return "Unknown";
  return titleCase(normalized);
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatSignedMinutes(value) {
  const number = numberOrNull(value);
  if (number == null) return null;
  const rounded = Math.round(number);
  if (rounded > 0) return `slow by ${rounded}m`;
  if (rounded < 0) return `${Math.abs(rounded)}m quicker`;
  return "no live delay";
}

function formatSource(value) {
  const source = String(value || "").trim().toLowerCase();
  if (source === "tomtom" || source === "tomtom_routes") return "TomTom";
  if (source === "google" || source === "google_routes") return "Google";
  if (source === "national_highways" || source === "nh" || source === "road_nh") return "National Highways";
  return source ? titleCase(source) : null;
}

function formatRoadJunction(evidence) {
  const from = evidence.from_junction || evidence.feature?.from_junction;
  const to = evidence.to_junction || evidence.feature?.to_junction;
  if (from && to) return `${from} -> ${to}`;
  return evidence.segment_key ? String(evidence.segment_key).replace(/_/g, " ") : null;
}

function formatLaneDetail(match) {
  const restricted = numberOrNull(match?.lanes_restricted);
  const open = numberOrNull(match?.lanes_open);
  if (restricted != null && open != null) return `${restricted} lane${restricted === 1 ? "" : "s"} restricted, ${open} open`;
  if (restricted != null) return `${restricted} lane${restricted === 1 ? "" : "s"} restricted`;
  const carriageway = match?.carriageway_type ? String(match.carriageway_type).replace(/_/g, " ") : null;
  return carriageway || null;
}

function roadReason(alert, evidence, feature) {
  const raw = alert.raw || {};
  const nhMatch = evidence.nh_matches?.[0] || feature.nh_matches?.[0] || null;
  const source = formatSource(evidence.source || feature.source || raw.source || alert.system);
  const delay =
    formatSignedMinutes(evidence.metric_value_mins) ||
    formatSignedMinutes(raw.last_metric_value_mins) ||
    formatSignedMinutes(feature.max_traffic_delay_mins) ||
    formatSignedMinutes(evidence.tomtom?.delay_mins) ||
    formatSignedMinutes(evidence.google?.delay_mins);
  const junction = formatRoadJunction({ ...feature, ...evidence });
  const incidentTitle = nhMatch?.title || evidence.reason_text || feature.reason_text || null;
  const laneDetail = formatLaneDetail(nhMatch);

  return [delay, source, incidentTitle, laneDetail, junction].filter(Boolean).slice(0, 4).join(" · ");
}

function railReason(alert, evidence, feature) {
  const raw = alert.raw || {};
  const incident = evidence.incidents?.[0] || null;
  const cancelled = numberOrNull(feature.cancelled_count ?? raw.cancelled_services_count);
  const delayed = numberOrNull(feature.delayed_count ?? raw.delayed_services_count);
  const maxDelay = numberOrNull(feature.max_delay_mins ?? raw.max_delay_mins);
  const parts = [];
  if (cancelled && cancelled > 0) parts.push(`${cancelled} cancelled service${cancelled === 1 ? "" : "s"}`);
  if (delayed && delayed > 0) parts.push(`${delayed} delayed service${delayed === 1 ? "" : "s"}`);
  if (maxDelay && maxDelay > 0) parts.push(`max delay ${Math.round(maxDelay)}m`);
  if (incident?.summary || incident?.title) parts.push(incident.summary || incident.title);
  if (evidence.reason_text || feature.reason_text) parts.push(evidence.reason_text || feature.reason_text);
  return parts.filter(Boolean).slice(0, 3).join(" · ");
}

function flightReason(evidence, feature) {
  const examples = evidence.examples || [];
  const cancelled = numberOrNull(feature.cancelled);
  const severe = numberOrNull(feature.severe_60_plus);
  const delayed = numberOrNull(feature.delayed_total);
  const nextHour = numberOrNull(feature.next_hour_flights);
  const parts = [];
  if (cancelled != null) parts.push(`${cancelled} cancelled`);
  if (severe != null) parts.push(`${severe} severe delays`);
  else if (delayed != null) parts.push(`${delayed} delayed`);
  if (nextHour != null) parts.push(`${nextHour} flights next hour`);
  if (examples[0]?.flight_number || examples[0]?.status_text) {
    const example = [examples[0].flight_number, examples[0].status_text].filter(Boolean).join(" ");
    if (example) parts.push(example);
  }
  return parts.filter(Boolean).slice(0, 3).join(" · ");
}

function reasonFromEvidence(alert) {
  const evidence = alert.evidence || alert.evidence_json || {};
  const feature = evidence.feature || alert.raw?.feature_json || alert.feature_json || {};
  if (alert.system === "road" || alert.system === "road_nh") {
    const reason = roadReason(alert, evidence, feature);
    if (reason) return reason;
  }
  if (alert.system === "rail") {
    const reason = railReason(alert, evidence, feature);
    if (reason) return reason;
  }
  if (alert.system === "flight") {
    const reason = flightReason(evidence, feature);
    if (reason) return reason;
  }
  const parts = [
    evidence.reason_text,
    feature.reason_text,
    evidence.metric_value != null ? `metric ${evidence.metric_value}` : null,
    alert.category ? titleCase(alert.category) : null,
    alert.source_freshness ? `Source ${String(alert.source_freshness).replace(/_/g, " ")}` : null,
  ].filter(Boolean);
  if (parts.length) return parts.slice(0, 2).join(" · ");
  const affected = alert.affected_entities || [];
  if (affected.length) return affected.filter(Boolean).slice(0, 2).join(" · ");
  return "Unified alert signal";
}

function normalizeActiveAlert(item, airport) {
  const severity = normalizeSeverity(item.severity || item.current_severity || item.severity_after);
  return {
    id: `${airport}:${item.system || "alert"}:${item.target_key || item.title || alertTime(item)}`,
    airport,
    system: item.system || "alert",
    time: alertTime(item),
    description: item.summary || item.title || item.display_name || item.target_key || "Airport alert",
    reason: reasonFromEvidence(item),
    severity,
    status: item.status || "open",
    confidence: item.confidence || null,
  };
}

function normalizeEvent(row, airport, system) {
  const severity = normalizeSeverity(row.severity_after || row.severity || row.current_severity || "cleared");
  const description = row.title || row.display_name || row.target_key || `${system.label} alert`;
  const feature = row.feature_json || {};
  const evidence = row.evidence_json || {};
  return {
    id: `${airport}:${system.key}:${row.event_id || row.target_key || row.observed_at_utc}`,
    airport,
    system: system.key,
    time: row.observed_at_utc || row.updated_at || row.created_at,
    description,
    reason: evidence.reason_text || feature.reason_text || row.event_type || "Cleared today",
    severity,
    status: row.status_after || row.status || row.event_type || "cleared",
    confidence: null,
  };
}

function sortAlerts(a, b) {
  const byTime = timestamp(b) - timestamp(a);
  if (byTime !== 0) return byTime;
  return (severityRank[b.severity] || 0) - (severityRank[a.severity] || 0);
}

async function loadUnifiedAirport(airport) {
  const res = await api.get(`/api/airport-alerts/airports/${airport}/unified?status=open&limit=150`);
  return (res.data?.items || []).map((item) => normalizeActiveAlert(item, airport));
}

async function loadClearedTodayAirport(airport) {
  const targetDay = todayKey();
  const responses = await Promise.allSettled(
    SYSTEM_DASHBOARDS.map(async (system) => {
      const url = system.path.replace(":airport", airport);
      const res = await api.get(url, { params: { status: "all", current_limit: 150, event_limit: 150 } });
      const events = (res.data?.recent_events || []).map((row) => normalizeEvent(row, airport, system));
      if (system.key !== "road") return events;
      return [
        ...events,
        ...(res.data?.nh_alerts?.recent_events || []).map((row) =>
          normalizeEvent(row, airport, { key: "road_nh", label: "Road NH" })
        ),
      ];
    })
  );

  return responses
    .flatMap((result) => (result.status === "fulfilled" ? result.value : []))
    .filter((event) => todayKey(event.time) === targetDay)
    .filter((event) => String(event.status || "").toLowerCase().includes("clear") || event.status === "resolved")
    .sort(sortAlerts);
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`ia-stat ia-stat-${tone || "neutral"}`}>
      <div>{label}</div>
      <strong>{value}</strong>
    </div>
  );
}

function SeverityPill({ value }) {
  const severity = normalizeSeverity(value);
  return <span className={`ia-pill ia-pill-${severity}`}>{statusLabel(severity)}</span>;
}

function AlertRow({ alert, muted }) {
  return (
    <div className={`ia-row ${muted ? "ia-row-muted" : ""}`}>
      <div className="ia-time">{formatDateTime(alert.time)}</div>
      <div className="ia-airport">{alert.airport}</div>
      <div className="ia-system">{titleCase(alert.system)}</div>
      <div className="ia-desc">{alert.description}</div>
      <div className="ia-reason">{alert.reason}</div>
      <div className="ia-status">
        <SeverityPill value={alert.severity} />
      </div>
    </div>
  );
}

function EmptyState({ children }) {
  return <div className="ia-empty">{children}</div>;
}

export default function IntelligenceAlertingView() {
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [clearedToday, setClearedToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [showCleared, setShowCleared] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const [activeResults, clearedResults] = await Promise.all([
        Promise.all(AIRPORTS.map(loadUnifiedAirport)),
        Promise.all(AIRPORTS.map(loadClearedTodayAirport)),
      ]);
      setActiveAlerts(activeResults.flat().sort(sortAlerts));
      setClearedToday(clearedResults.flat().sort(sortAlerts));
      setLastLoadedAt(new Date().toISOString());
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to load alerting view");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const stats = useMemo(() => {
    const active = activeAlerts;
    return {
      total: active.length,
      red: active.filter((item) => normalizeSeverity(item.severity) === "red" || normalizeSeverity(item.severity) === "critical_red").length,
      amber: active.filter((item) => normalizeSeverity(item.severity) === "amber").length,
      light: active.filter((item) => normalizeSeverity(item.severity) === "light_green").length,
      systems: new Set(active.map((item) => item.system)).size,
    };
  }, [activeAlerts]);

  return (
    <div className="ia-page">
      <Helmet>
        <title>Intelligence Alerting | Airport Journey Planner</title>
        <meta
          name="description"
          content="Operational alerting dashboard using unified intelligent airport alert signals."
        />
      </Helmet>

      <style>{`
        .ia-page {
          min-height: 100vh;
          background:
            radial-gradient(circle at 18% 0%, rgba(37, 99, 235, 0.2), transparent 34rem),
            linear-gradient(180deg, #071827 0%, #06111d 100%);
          color: #eaf2ff;
          padding: 28px;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .ia-shell {
          max-width: 1500px;
          margin: 0 auto;
        }
        .ia-page .ia-header {
          display: flex;
          justify-content: space-between;
          gap: 24px;
          align-items: flex-start;
          margin-bottom: 22px;
          padding: 0;
          background: transparent;
          border: 0;
          box-shadow: none;
        }
        .ia-kicker {
          color: #7dd3fc;
          font-weight: 800;
          font-size: 12px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .ia-title {
          margin: 6px 0 6px;
          font-size: clamp(34px, 5vw, 58px);
          line-height: 0.96;
          letter-spacing: 0;
        }
        .ia-subtitle {
          margin: 0;
          color: #a9b9ce;
          font-size: 15px;
        }
        .ia-refresh {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 8px;
          color: #b6c6d9;
          font-size: 13px;
        }
        .ia-refresh strong {
          color: #f8fbff;
        }
        .ia-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #22c55e;
          box-shadow: 0 0 18px rgba(34, 197, 94, 0.8);
          display: inline-block;
          margin-right: 8px;
        }
        .ia-stats {
          display: grid;
          grid-template-columns: repeat(5, minmax(120px, 1fr));
          gap: 12px;
          margin-bottom: 18px;
        }
        .ia-stat {
          border: 1px solid rgba(148, 163, 184, 0.22);
          background: rgba(11, 31, 51, 0.78);
          border-radius: 12px;
          padding: 15px 16px;
          box-shadow: 0 16px 42px rgba(0, 0, 0, 0.22);
        }
        .ia-stat div {
          color: #91a7bd;
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .ia-stat strong {
          display: block;
          margin-top: 8px;
          font-size: 28px;
          line-height: 1;
        }
        .ia-stat-red { border-color: rgba(248, 113, 113, 0.48); }
        .ia-stat-amber { border-color: rgba(245, 158, 11, 0.46); }
        .ia-stat-light { border-color: rgba(74, 222, 128, 0.42); }
        .ia-card {
          border: 1px solid rgba(148, 163, 184, 0.25);
          background: rgba(6, 20, 34, 0.88);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 22px 70px rgba(0, 0, 0, 0.26);
          margin-bottom: 18px;
        }
        .ia-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 18px 20px;
          border-bottom: 1px solid rgba(148, 163, 184, 0.18);
        }
        .ia-card-header h2 {
          margin: 0;
          font-size: 20px;
          letter-spacing: 0;
        }
        .ia-card-header span {
          color: #96a9bf;
          font-size: 13px;
          font-weight: 800;
        }
        .ia-table-head,
        .ia-row {
          display: grid;
          grid-template-columns: 132px 72px 116px minmax(260px, 1.35fr) minmax(260px, 1fr) 118px;
          gap: 14px;
          align-items: center;
        }
        .ia-table-head {
          padding: 12px 20px;
          color: #91a7bd;
          font-size: 12px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: rgba(15, 35, 56, 0.72);
          border-bottom: 1px solid rgba(148, 163, 184, 0.16);
        }
        .ia-row {
          padding: 15px 20px;
          border-bottom: 1px solid rgba(148, 163, 184, 0.14);
          background: rgba(12, 31, 51, 0.46);
        }
        .ia-row:nth-child(odd) {
          background: rgba(9, 25, 43, 0.54);
        }
        .ia-row:last-child {
          border-bottom: none;
        }
        .ia-row-muted {
          opacity: 0.78;
        }
        .ia-time,
        .ia-system,
        .ia-airport {
          color: #cfdef0;
          font-weight: 800;
          font-size: 13px;
        }
        .ia-airport {
          width: max-content;
          padding: 5px 8px;
          border-radius: 8px;
          background: rgba(59, 130, 246, 0.14);
          color: #bfdbfe;
        }
        .ia-desc {
          color: #ffffff;
          font-weight: 900;
          line-height: 1.35;
        }
        .ia-reason {
          color: #b7c8db;
          line-height: 1.35;
          font-size: 13px;
        }
        .ia-status {
          display: flex;
          justify-content: flex-end;
        }
        .ia-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 84px;
          padding: 8px 11px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 900;
          border: 1px solid rgba(148, 163, 184, 0.24);
        }
        .ia-pill-red,
        .ia-pill-critical_red {
          color: #fecaca;
          background: rgba(127, 29, 29, 0.62);
          border-color: rgba(248, 113, 113, 0.58);
        }
        .ia-pill-amber {
          color: #fde68a;
          background: rgba(120, 53, 15, 0.62);
          border-color: rgba(245, 158, 11, 0.58);
        }
        .ia-pill-light_green,
        .ia-pill-green {
          color: #bbf7d0;
          background: rgba(20, 83, 45, 0.58);
          border-color: rgba(74, 222, 128, 0.44);
        }
        .ia-pill-unknown,
        .ia-pill-cleared {
          color: #dbeafe;
          background: rgba(30, 41, 59, 0.7);
        }
        .ia-empty {
          padding: 34px 20px;
          color: #99adbf;
          text-align: center;
          font-weight: 800;
        }
        .ia-error {
          border: 1px solid rgba(248, 113, 113, 0.45);
          background: rgba(127, 29, 29, 0.3);
          color: #fecaca;
          padding: 12px 14px;
          border-radius: 12px;
          margin-bottom: 14px;
        }
        .ia-button {
          border: 1px solid rgba(96, 165, 250, 0.45);
          background: rgba(37, 99, 235, 0.18);
          color: #dbeafe;
          border-radius: 10px;
          padding: 10px 13px;
          font-weight: 900;
          cursor: pointer;
        }
        .ia-loading {
          color: #93c5fd;
          font-weight: 800;
        }
        @media (max-width: 980px) {
          .ia-page { padding: 18px; }
          .ia-header { flex-direction: column; }
          .ia-refresh { align-items: flex-start; }
          .ia-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .ia-table-head { display: none; }
          .ia-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          .ia-status { justify-content: flex-start; }
        }
      `}</style>

      <main className="ia-shell">
        <header className="ia-header">
          <div>
            <div className="ia-kicker">Intelligent Alerting</div>
            <h1 className="ia-title">Airport alert control</h1>
            <p className="ia-subtitle">
              Unified active alarms from intelligent alerting, plus today&apos;s cleared signals for audit.
            </p>
          </div>
          <div className="ia-refresh">
            <div>
              <span className="ia-dot" />
              Auto refresh every 60 seconds
            </div>
            <strong>{lastLoadedAt ? `Last loaded ${formatDateTime(lastLoadedAt)}` : "Waiting for first load"}</strong>
            {loading ? <span className="ia-loading">Loading alerts...</span> : null}
          </div>
        </header>

        {error ? <div className="ia-error">{error}</div> : null}

        <section className="ia-stats" aria-label="Active alert summary">
          <StatCard label="Active alarms" value={stats.total} />
          <StatCard label="Red" value={stats.red} tone="red" />
          <StatCard label="Amber" value={stats.amber} tone="amber" />
          <StatCard label="Light" value={stats.light} tone="light" />
          <StatCard label="Systems" value={stats.systems} />
        </section>

        <section className="ia-card">
          <div className="ia-card-header">
            <h2>Active Alarms</h2>
            <span>Latest first · LHR and EMA · open until cleared</span>
          </div>
          <div className="ia-table-head">
            <div>Date/time</div>
            <div>Airport</div>
            <div>System</div>
            <div>Alarm description</div>
            <div>Reason</div>
            <div>Status</div>
          </div>
          {activeAlerts.length ? (
            activeAlerts.map((alert) => <AlertRow key={alert.id} alert={alert} />)
          ) : (
            <EmptyState>No active intelligent alerts are currently open.</EmptyState>
          )}
        </section>

        <section className="ia-card">
          <div className="ia-card-header">
            <h2>Today&apos;s Cleared Alarms</h2>
            <button className="ia-button" type="button" onClick={() => setShowCleared((value) => !value)}>
              {showCleared ? "Hide cleared" : `Show cleared (${clearedToday.length})`}
            </button>
          </div>
          {showCleared ? (
            <>
              <div className="ia-table-head">
                <div>Date/time</div>
                <div>Airport</div>
                <div>System</div>
                <div>Alarm description</div>
                <div>Reason</div>
                <div>Status</div>
              </div>
              {clearedToday.length ? (
                clearedToday.map((alert) => <AlertRow key={alert.id} alert={alert} muted />)
              ) : (
                <EmptyState>No cleared alert events found for today.</EmptyState>
              )}
            </>
          ) : null}
        </section>
      </main>
    </div>
  );
}
